/**
 * Validation and inline parsing for admin-authored legal document content.
 *
 * Legal copy is stored as a closed union of blocks (see `@tabbio/types`), never
 * as HTML. Rich text inside a block uses a deliberately small inline syntax:
 *
 *   **bold**                      → emphasised run
 *   `code`                        → monospace run (endpoints, file paths)
 *   [label](https://example.com)  → external link
 *   [label](/pricing)             → internal, locale-aware link
 *   [label](mailto:a@b.com)       → mail link
 *   a@b.com                       → auto-linked mail link
 *
 * A link label may itself contain **bold** and `code`, but not another link;
 * `parseLegalInlineLabel` handles that second, shallower pass.
 *
 * Anything else — raw HTML, `javascript:` targets, protocol-relative URLs — is
 * rejected by the schema and rendered as literal text by the parser. The two
 * halves are intentionally asymmetric:
 *
 *   `parseLegalInline` is TOTAL: it never throws, so a page that somehow holds
 *   older or malformed content still renders rather than 500ing. Legal pages
 *   are load-bearing and must degrade to plain text, never to an error.
 *
 *   `findLegalInlineIssues` is STRICT: it is what the write path enforces, so
 *   nothing malformed is accepted in the first place.
 *
 * The renderer consuming these nodes must build React elements — never
 * `dangerouslySetInnerHTML`. An admin account is not a trusted HTML author.
 */

import { z } from "zod";
import type {
  LegalBlock,
  LegalDocumentContent,
  LegalLeafBlock,
  LegalSectionContent,
} from "@tabbio/types";

// ── Inline syntax ────────────────────────────────────────────────────────────

/** Nodes that may appear anywhere, including inside a link label. */
export type LegalInlineLabelNode =
  | { kind: "text"; value: string }
  | { kind: "bold"; value: string }
  | { kind: "code"; value: string };

export type LegalInlineNode =
  | LegalInlineLabelNode
  | { kind: "link"; href: string; label: string }
  | { kind: "internalLink"; href: string; label: string }
  | { kind: "assetLink"; href: string; label: string }
  | { kind: "mail"; address: string; href: string; label: string };

/**
 * Alternation order matters: the link branch must precede the code branch so a
 * backtick inside a label (`` [`/llms.txt`](https://…) ``) does not win first.
 */
const INLINE_PATTERN =
  /\*\*([^*\n]+)\*\*|\[([^\]\n]+)\]\(([^)\s]+)\)|`([^`\n]+)`|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

const LABEL_PATTERN = /\*\*([^*\n]+)\*\*|`([^`\n]+)`/g;

/** `https://…` only. Plain `http://` is refused so notices can't be downgraded. */
const EXTERNAL_HREF = /^https:\/\/[^\s<>"'`]+$/;

/**
 * A site-relative page path: `/pricing`, `/privacy-policy`, `/tools/cv-checker`.
 * Requiring the second character to be a lowercase letter or digit is what
 * rejects protocol-relative `//evil.com`, and no segment may contain a `.`, so
 * `..` traversal cannot appear either. These render through the locale-aware
 * router, which prefixes the active locale.
 */
const INTERNAL_HREF = /^\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/;

/**
 * A served file or route handler: `/llms.txt`, `/rsl.xml`, `/en/ai-disclosure.md`.
 * Distinguished from a page path by its extension, and deliberately kept off
 * the locale-aware router — prefixing a locale onto `/llms.txt` would 404 it.
 * Each `.` must be followed by more characters, so `..` still cannot appear.
 */
const ASSET_HREF =
  /^\/[a-z0-9]+(?:[-.][a-z0-9]+)*(?:\/[a-z0-9]+(?:[-.][a-z0-9]+)*)*\.[a-z0-9]{2,5}$/;

/** `mailto:` with an optional pre-filled subject/body query. */
const MAILTO_HREF =
  /^mailto:([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})(?:\?[A-Za-z0-9%._~!$&'()*+,;=:@/?-]*)?$/;

export const LEGAL_INLINE_MAX_LENGTH = 4000;

function classifyHref(
  href: string,
  label: string,
): LegalInlineNode | null {
  const mail = MAILTO_HREF.exec(href);
  if (mail) return { kind: "mail", address: mail[1], href, label };
  if (EXTERNAL_HREF.test(href)) return { kind: "link", href, label };
  if (INTERNAL_HREF.test(href)) return { kind: "internalLink", href, label };
  if (ASSET_HREF.test(href)) return { kind: "assetLink", href, label };
  return null;
}

/**
 * Tokenize inline copy into renderable nodes. Never throws: an unrecognised
 * link target degrades to the literal source text.
 */
export function parseLegalInline(text: string): LegalInlineNode[] {
  const nodes: LegalInlineNode[] = [];
  let cursor = 0;

  const pushText = (value: string) => {
    if (!value) return;
    const previous = nodes[nodes.length - 1];
    if (previous?.kind === "text") previous.value += value;
    else nodes.push({ kind: "text", value });
  };

  INLINE_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    pushText(text.slice(cursor, match.index));
    cursor = match.index + match[0].length;

    const [raw, bold, linkLabel, linkHref, code, bareEmail] = match;
    if (bold !== undefined) {
      nodes.push({ kind: "bold", value: bold });
    } else if (linkLabel !== undefined && linkHref !== undefined) {
      const node = classifyHref(linkHref, linkLabel);
      if (node) nodes.push(node);
      else pushText(raw);
    } else if (code !== undefined) {
      nodes.push({ kind: "code", value: code });
    } else if (bareEmail !== undefined) {
      nodes.push({
        kind: "mail",
        address: bareEmail,
        href: `mailto:${bareEmail}`,
        label: bareEmail,
      });
    }
  }
  pushText(text.slice(cursor));

  return nodes;
}

/**
 * Second, shallower pass for the text inside a link label: bold and code only,
 * so labels cannot nest links.
 */
export function parseLegalInlineLabel(label: string): LegalInlineLabelNode[] {
  const nodes: LegalInlineLabelNode[] = [];
  let cursor = 0;

  const pushText = (value: string) => {
    if (!value) return;
    const previous = nodes[nodes.length - 1];
    if (previous?.kind === "text") previous.value += value;
    else nodes.push({ kind: "text", value });
  };

  LABEL_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = LABEL_PATTERN.exec(label)) !== null) {
    pushText(label.slice(cursor, match.index));
    cursor = match.index + match[0].length;
    const [, bold, code] = match;
    if (bold !== undefined) nodes.push({ kind: "bold", value: bold });
    else if (code !== undefined) nodes.push({ kind: "code", value: code });
  }
  pushText(label.slice(cursor));

  return nodes;
}

/**
 * Strict counterpart to `parseLegalInline`, used by the write path. Returns a
 * human-readable issue per problem so the admin editor can point at the exact
 * mistake instead of failing with a generic "invalid content".
 */
export function findLegalInlineIssues(text: string): string[] {
  const issues: string[] = [];

  if (text.includes("<")) {
    issues.push(
      "HTML is not allowed in legal copy. Use **bold** and [label](url) instead.",
    );
  }
  if ((text.match(/\*\*/g)?.length ?? 0) % 2 !== 0) {
    issues.push("Unbalanced ** markers — bold runs must be opened and closed.");
  }
  if ((text.match(/`/g)?.length ?? 0) % 2 !== 0) {
    issues.push("Unbalanced ` markers — code runs must be opened and closed.");
  }

  INLINE_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    const [, , linkLabel, linkHref] = match;
    if (linkLabel === undefined || linkHref === undefined) continue;
    if (!classifyHref(linkHref, linkLabel)) {
      issues.push(
        `Unsupported link target "${linkHref}". Use https://…, a site path like /pricing, a file like /llms.txt, or mailto:…`,
      );
    }
  }

  return issues;
}

/** Visible text of an inline string, with all markup resolved to its label. */
export function legalInlineToPlainText(text: string): string {
  return parseLegalInline(text)
    .map((node) => {
      switch (node.kind) {
        case "text":
        case "bold":
        case "code":
          return node.value;
        default:
          // Labels carry their own bold/code markers; strip those too.
          return parseLegalInlineLabel(node.label)
            .map((part) => part.value)
            .join("");
      }
    })
    .join("");
}

// ── Block schema ─────────────────────────────────────────────────────────────

const inlineText = (max = LEGAL_INLINE_MAX_LENGTH) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .superRefine((value, context) => {
      for (const message of findLegalInlineIssues(value)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message });
      }
    });

/** Short labels are plain: no links, no bold, so headings stay scannable. */
const plainText = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((value) => !value.includes("<"), {
      message: "HTML is not allowed in legal copy.",
    });

export const legalParagraphBlockSchema = z
  .object({ type: z.literal("paragraph"), text: inlineText() })
  .strict();

export const legalListBlockSchema = z
  .object({
    type: z.literal("list"),
    items: z.array(inlineText(1500)).min(1).max(40),
  })
  .strict();

export const legalCalloutBlockSchema = z
  .object({
    type: z.literal("callout"),
    label: plainText(160),
    text: inlineText(),
  })
  .strict();

export const legalCapsBlockSchema = z
  .object({ type: z.literal("caps"), text: inlineText() })
  .strict();

export const legalCardsBlockSchema = z
  .object({
    type: z.literal("cards"),
    items: z
      .array(
        z
          .object({ term: plainText(160), def: inlineText(1500) })
          .strict(),
      )
      .min(1)
      .max(24),
  })
  .strict();

export const legalLeafBlockSchema = z.discriminatedUnion("type", [
  legalParagraphBlockSchema,
  legalListBlockSchema,
  legalCalloutBlockSchema,
  legalCapsBlockSchema,
  legalCardsBlockSchema,
]);

/**
 * Depth is capped structurally rather than with `z.lazy`: a subsection holds
 * leaf blocks only, so the union stays finite and no recursion guard is needed.
 */
export const legalSubsectionBlockSchema = z
  .object({
    type: z.literal("subsection"),
    title: plainText(200),
    blocks: z.array(legalLeafBlockSchema).min(1).max(40),
  })
  .strict();

export const legalBlockSchema = z.discriminatedUnion("type", [
  legalParagraphBlockSchema,
  legalListBlockSchema,
  legalCalloutBlockSchema,
  legalCapsBlockSchema,
  legalCardsBlockSchema,
  legalSubsectionBlockSchema,
]);

export const legalSectionSchema = z
  .object({
    id: z
      .string()
      .trim()
      .regex(/^[a-z0-9][a-z0-9-]{0,63}$/, "Section ids are lowercase slugs."),
    num: z.number().int().min(1).max(200),
    title: plainText(300),
    blocks: z.array(legalBlockSchema).min(1).max(60),
  })
  .strict();

export const legalDocumentContentSchema = z
  .object({ sections: z.array(legalSectionSchema).min(1).max(120) })
  .strict()
  .superRefine((value, context) => {
    const seen = new Set<string>();
    for (const [index, section] of value.sections.entries()) {
      if (seen.has(section.id)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", index, "id"],
          message: `Duplicate section id "${section.id}" — TOC anchors must be unique.`,
        });
      }
      seen.add(section.id);
    }
  });

export const legalLocalizedContentSchema = z
  .object({
    en: legalDocumentContentSchema,
    ar: legalDocumentContentSchema,
  })
  .strict();

export const legalChangeSummarySchema = z
  .object({
    en: z.array(plainText(500)).max(12),
    ar: z.array(plainText(500)).max(12),
  })
  .strict();

// ── Plain-text projection ────────────────────────────────────────────────────

/**
 * Visible text of a block tree, one logical line per rendered element. This is
 * the comparison surface for the seed parity gate: if the blocks and the
 * hand-written TSX produce the same normalized text, no copy was lost in the
 * conversion.
 */
export function legalBlocksToPlainText(
  blocks: readonly LegalBlock[],
): string[] {
  const lines: string[] = [];
  const visit = (block: LegalBlock | LegalLeafBlock) => {
    switch (block.type) {
      case "paragraph":
      case "caps":
        lines.push(legalInlineToPlainText(block.text));
        break;
      case "list":
        for (const item of block.items) lines.push(legalInlineToPlainText(item));
        break;
      case "callout":
        lines.push(`${block.label} ${legalInlineToPlainText(block.text)}`);
        break;
      case "cards":
        for (const item of block.items) {
          lines.push(item.term);
          lines.push(legalInlineToPlainText(item.def));
        }
        break;
      case "subsection":
        lines.push(block.title);
        for (const child of block.blocks) visit(child);
        break;
    }
  };
  for (const block of blocks) visit(block);
  return lines.map((line) => line.replace(/\s+/g, " ").trim()).filter(Boolean);
}

export function legalSectionToPlainText(section: LegalSectionContent): string[] {
  return [section.title, ...legalBlocksToPlainText(section.blocks)];
}

export function legalDocumentToPlainText(
  content: LegalDocumentContent,
): string[] {
  return content.sections.flatMap(legalSectionToPlainText);
}
